import React, { useContext, useEffect, useImperativeHandle, useRef, useState } from "react";
import Bus from "@/utils/bus";
import DynamicWindow from "./DynamicWindow";
import CalendarNoticeCard from "../CalendarNoticeCard";
import TaskNoticeCard from "../TaskNoticeCard";
import SuperviseNoticeCard from "../SuperviseNoticeCard";
import SysNoticeCard from "../SysNoticeCard";
import { FocusSDK, isFocusEnv } from "@/utils";
import GlobalContext from "@/context/GlobalContext";
import { isNumber, pick } from "lodash";

interface winObj {
  cid: string;
  win: any;
}

export enum WinType {
  CALENDAR = "calendar",
  TASK = "task",
  SUPERVISE = "supervise",
  SYSNOTICE = "sysnotice"
}

const PopupWin = React.forwardRef(
  ({
    info,
    length,
    removePopupWin,
    screenWorkArea,
    winTimer,
    parentId,
    onWinClick,
    curWinRef
  }: {
    info: any;
    length: number;
    removePopupWin: Function;
    screenWorkArea: { width: number; height: number };
    winTimer: number;
    parentId: number;
    onWinClick: Function;
    curWinRef: any;
  }) => {
    const [winVisible, setWinVisible] = useState(true);
    const [params, setParams] = useState(info);
    const closeCurWindow = () => {
      setWinVisible(false);
      removePopupWin(params.cid);
    };
    useImperativeHandle(curWinRef, () => ({
      setParams
    }));
    if (!params.wintype) return null;
    const appName = process.env.REACT_APP_APP_NAME || "京办";
    return (
      <DynamicWindow
        // windowRef={curWinRef}
        name={appName}
        winId={params.cid}
        winOptions={{
          width: 350,
          height: 130,
          minWidth: 350,
          maxWidth: 350,
          minHeight: 130,
          maxHeight: 130,
          focusable: true,
          alwaysOnTop: true,
          x: (screenWorkArea.width || window.screen.width) - (350 + 30 + length * 10),
          y: 50 + length * 8
        }}
        // onClose={closeDynamicWindow}
        isPortalNode={false}
        showToolBar={false}
        visible={winVisible}
        parentId={parentId}
        onWinClick={onWinClick}
      >
        {params.wintype === WinType.CALENDAR && (
          <CalendarNoticeCard closeCurWindow={closeCurWindow} params={params} winTimer={winTimer} />
        )}
        {params.wintype === WinType.TASK && (
          <TaskNoticeCard closeCurWindow={closeCurWindow} params={params} winTimer={winTimer} />
        )}
        {params.wintype === WinType.SUPERVISE && (
          <SuperviseNoticeCard
            closeCurWindow={closeCurWindow}
            params={params}
            winTimer={winTimer}
          />
        )}
        {params.wintype === WinType.SYSNOTICE && (
          <SysNoticeCard closeCurWindow={closeCurWindow} params={params} winTimer={winTimer} />
        )}
      </DynamicWindow>
    );
  }
);

export default function ReminderWindow() {
  const [reminderList, setReminderList] = useState<winObj[]>([]);
  const parentIdRef = useRef<number | null>(null);
  const { localConfigData } = useContext(GlobalContext);
  const paramsMap: { [key: string]: string[] } = {
    [WinType.CALENDAR]: ["scheduleTitle", "beginTime", "endTime", "deepLink"],
    [WinType.TASK]: ["taskName", "endTime", "msgTitle", "deepLink"],
    [WinType.SUPERVISE]: [],
    [WinType.SYSNOTICE]: ["title", "fields", "deepLink"]
  };

  useEffect(() => {
    if (reminderList.length === 0 && isFocusEnv()) {
      FocusSDK.closeReminderWindow(parentIdRef.current);
    }
  }, [reminderList.length]);

  const removePopupWin = (cid: string) => {
    setReminderList((list) => {
      const idx = list.findIndex((item) => item.cid === cid);
      if (idx > -1) {
        return [...list.slice(0, idx), ...list.slice(idx + 1)];
      } else {
        return list;
      }
    });
  };

  const onWinClick = (wid: number) => {
    if (isFocusEnv()) {
      FocusSDK.reminderWindowSort(wid);
    }
  };

  const handlePopupWin = (
    params: any,
    length: number,
    screenWorkArea: { width: number; height: number },
    remindWinTimer: number,
    parentId: number
    // eslint-disable-next-line max-params
  ) => {
    const curWinRef: any = React.createRef();
    return (
      <PopupWin
        info={params}
        length={length}
        removePopupWin={removePopupWin}
        screenWorkArea={screenWorkArea}
        winTimer={isNumber(remindWinTimer) ? remindWinTimer : 1}
        parentId={parentId}
        onWinClick={onWinClick}
        curWinRef={curWinRef}
      />
    );
  };

  const popUpNotification = async (params: any) => {
    // FocusSDK.printLog("popUpNotification params==>", params);
    if (
      !(
        (params.wintype === WinType.CALENDAR ||
          params.wintype === WinType.TASK ||
          params.wintype === WinType.SUPERVISE ||
          params.wintype === WinType.SYSNOTICE) &&
        isFocusEnv()
      )
    ) {
      return;
    }
    try {
      const parentId = await FocusSDK.getReminderWindowId();
      const screenWorkArea = await FocusSDK.getScreenWorkAreaSize();
      parentIdRef.current = parentId;
      const remindWinTimer = Number(localConfigData?.settings?.remindWinTimer || "1");
      setReminderList((list) => {
        const idx = list.findIndex((item) => item.cid === params.cid);
        if (idx < 0) {
          const win = handlePopupWin(params, list.length, screenWorkArea, remindWinTimer, parentId);
          return [...list, { cid: params.cid, win }];
        } else {
          // 如果提醒内容有更新则刷新弹窗
          const oldInfo = pick(list[idx].win.props.info, paramsMap[params.wintype]);
          const newInfo = pick(params, paramsMap[params.wintype]);
          // console.log(
          //   "paramsMap===>>>",
          //   paramsMap,
          //   list[idx].win.props.info,
          //   paramsMap[params.wintype],
          //   newInfo,
          //   oldInfo,
          //   JSON.stringify(newInfo) !== JSON.stringify(oldInfo)
          // );
          if (JSON.stringify(newInfo) !== JSON.stringify(oldInfo)) {
            list[idx].win.props.curWinRef.current.setParams(params);
          }
          return [...list];
        }
      });
    } catch (error) {
      FocusSDK.printLog("popUpNotification error==>", error);
    }
  };

  useEffect(() => {
    Bus.on("reminder:pop-up-notification", popUpNotification);
    return () => {
      Bus.off("reminder:pop-up-notification", popUpNotification);
      isFocusEnv() && FocusSDK.closeReminderWindow(parentIdRef.current);
      parentIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      {!!reminderList.length &&
        reminderList.map((item: any, index: number) => <div key={item.cid}>{item.win}</div>)}
    </div>
  );
}
